/**
 * Pipecat Cloud + Daily WebRTC hanghívás (CDN daily-js, nincs npm függőség).
 * Laravel POST /widget/voice/session adja a daily_room + daily_token-t.
 * RTVI app-message: transcript / bot szöveg a widget chatbe.
 */

const DAILY_CDN = 'https://unpkg.com/@daily-co/daily-js@0.91.0';

/**
 * @returns {Promise<typeof window.DailyIframe>}
 */
function loadDaily() {
    if (window.DailyIframe) {
        return Promise.resolve(window.DailyIframe);
    }

    if (! window.__chattDailyLoader) {
        window.__chattDailyLoader = createDailyLoader();
    }

    return window.__chattDailyLoader;
}

/**
 * A Daily SDK betöltése a panel megnyitásakor indul, hogy a mikrofon
 * kattintáskor már a memóriában legyen (a CDN kör kiesik a kritikus útból).
 */
export function preloadVoicePipecat() {
    loadDaily().catch(() => {});
}

/**
 * Mikrofon engedély + eszköz előmelegítése a session hívással párhuzamosan.
 * A kapott sávot közvetlenül a Daily kapja meg, így nincs második getUserMedia.
 *
 * @returns {Promise<MediaStreamTrack|null>}
 */
export async function warmUpVoiceMicrophone() {
    try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });

        return stream.getAudioTracks()[0] ?? null;
    } catch {
        return null;
    }
}

/**
 * Előmelegített, de fel nem használt mikrofon sáv elengedése (ne maradjon égve a jelző).
 *
 * @param {Promise<MediaStreamTrack|null>|null} trackPromise
 */
export function releaseWarmMicrophone(trackPromise) {
    if (! trackPromise) {
        return;
    }
    Promise.resolve(trackPromise)
        .then((track) => track?.stop())
        .catch(() => {});
}

/**
 * Távoli hang `<audio>` feloldása a mic kattintás user gesture-jén belül.
 * A Daily track később érkezik — ha az elem csak akkor jönne létre, a play() autoplay miatt elbukik
 * (felirat van, hang nincs).
 *
 * @returns {HTMLAudioElement}
 */
export function unlockVoiceRemoteSpeaker() {
    let el = window.__chattVoiceSpeaker;
    if (el && ! document.body.contains(el)) {
        window.__chattVoiceSpeaker = null;
        el = null;
    }
    if (el) {
        el.play().catch(() => {});

        return el;
    }

    el = document.createElement('audio');
    el.autoplay = true;
    el.playsInline = true;
    el.setAttribute('playsinline', 'true');
    el.setAttribute('webkit-playsinline', 'true');
    el.muted = false;
    el.volume = 1;
    // Minimális néma WAV — egyes böngészők csak src-cel engedik a play()-t gesture alatt.
    el.src = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA';
    document.body.appendChild(el);
    el.play().catch(() => {});
    window.__chattVoiceSpeaker = el;

    return el;
}

/**
 * @returns {Promise<typeof window.DailyIframe>}
 */
function createDailyLoader() {
    return new Promise((resolve, reject) => {
        const existing = document.querySelector('script[data-chatt-daily="1"]');
        if (existing) {
            existing.addEventListener('load', () => resolve(window.DailyIframe));
            existing.addEventListener('error', () => reject(new Error('Daily.js betöltés sikertelen')));

            return;
        }

        const script = document.createElement('script');
        script.src = DAILY_CDN;
        script.async = true;
        script.dataset.chattDaily = '1';
        script.onload = () => {
            if (! window.DailyIframe) {
                reject(new Error('Daily.js nem elérhető'));

                return;
            }
            resolve(window.DailyIframe);
        };
        script.onerror = () => reject(new Error('Daily.js CDN hiba'));
        document.head.appendChild(script);
    });
}

/**
 * @param {unknown} raw
 * @returns {{ type: string, text?: string, final?: boolean, form_id?: string, form_payload?: object }|null}
 */
function parseVoiceAppMessage(raw) {
    let msg = raw;
    if (typeof msg === 'string') {
        try {
            msg = JSON.parse(msg);
        } catch {
            return null;
        }
    }
    if (! msg || typeof msg !== 'object') {
        return null;
    }

    // Saját bridge (LaravelVoiceLLMService → TransportMessageUrgentFrame)
    if (msg.label === 'chatt-voice') {
        if (msg.type === 'user-transcript' && typeof msg.text === 'string' && msg.text.trim()) {
            return { type: 'user-transcription', text: msg.text.trim(), final: true };
        }
        if (msg.type === 'user-transcript-partial' && typeof msg.text === 'string' && msg.text.trim()) {
            return { type: 'user-transcription', text: msg.text.trim(), final: false };
        }
        if (msg.type === 'bot-reply' && typeof msg.text === 'string' && msg.text.trim()) {
            return { type: 'bot-text', text: msg.text.trim(), final: true };
        }
        if (msg.type === 'bot-reply-partial' && typeof msg.text === 'string' && msg.text.trim()) {
            return { type: 'bot-text', text: msg.text.trim(), final: false };
        }
        if (
            msg.type === 'lead_form'
            && typeof msg.form_id === 'string'
            && msg.form_id
            && msg.form_payload
            && typeof msg.form_payload === 'object'
        ) {
            return {
                type: 'lead_form',
                form_id: msg.form_id,
                form_payload: msg.form_payload,
            };
        }
        if (msg.type === 'handover_status' && typeof msg.status === 'string' && msg.status) {
            return {
                type: 'handover_status',
                status: msg.status,
                message: typeof msg.message === 'string' ? msg.message : null,
                bot_silenced: msg.bot_silenced === true,
                end_voice: msg.end_voice === true,
                voice_to_text_notice: typeof msg.voice_to_text_notice === 'string'
                    ? msg.voice_to_text_notice
                    : null,
            };
        }

        return null;
    }

    if (msg.label !== 'rtvi-ai' || typeof msg.type !== 'string') {
        return null;
    }

    // Szöveg csak a saját 'chatt-voice' üzenetekből: az RTVI ugyanazt a turn-t
    // többször is elküldi (user-transcription, bot-transcription, bot-llm-text,
    // bot-output, illetve bot-tts-text szavanként) — ezek duplikálnák a chatet.
    if (msg.type === 'bot-tts-started') {
        return { type: 'bot-tts-started', text: '' };
    }
    if (msg.type === 'bot-tts-stopped') {
        return { type: 'bot-tts-stopped', text: '' };
    }

    return null;
}

/**
 * @param {{
 *   onState: (state: 'connecting'|'listening'|'processing'|'speaking'|'idle') => void,
 *   onError: (message: string) => void,
 *   onReady?: () => void,
 *   onEnded?: () => void,
 *   onUserTranscript?: (text: string, final: boolean) => void,
 *   onBotText?: (text: string, final: boolean) => void,
 *   onLeadForm?: (event: { form_id: string, form_payload: object }) => void,
 *   onHandoverStatus?: (event: { status: string, message?: string|null, bot_silenced?: boolean }) => void,
 *   onTiming?: (label: string) => void,
 * }} handlers
 */
export function createVoicePipecatSession(handlers) {
    /** @type {ReturnType<typeof window.DailyIframe.createCallObject>|null} */
    let call = null;
    /** @type {HTMLAudioElement|null} */
    let remoteAudio = null;
    /** @type {MediaStreamTrack|null} */
    let warmTrack = null;
    let active = false;
    let stopped = false;

    function stopWarmTrack() {
        if (! warmTrack) {
            return;
        }
        try {
            warmTrack.stop();
        } catch {
            // ignore
        }
        warmTrack = null;
    }

    function cleanupAudio() {
        if (remoteAudio) {
            try {
                remoteAudio.pause();
                remoteAudio.removeAttribute('src');
                remoteAudio.srcObject = null;
            } catch {
                // ignore
            }
            if (remoteAudio.parentNode) {
                try {
                    remoteAudio.parentNode.removeChild(remoteAudio);
                } catch {
                    // ignore
                }
            }
            if (window.__chattVoiceSpeaker === remoteAudio) {
                window.__chattVoiceSpeaker = null;
            }
            remoteAudio = null;
        }
    }

    function kickRemotePlayback() {
        if (! remoteAudio) {
            return;
        }
        try {
            remoteAudio.muted = false;
            remoteAudio.volume = 1;
            const play = () => remoteAudio.play().catch(() => {});
            play();
        } catch {
            // ignore
        }
    }

    /**
     * @param {MediaStreamTrack} track
     */
    function attachRemoteAudio(track) {
        // Gesture alatt feloldott elem újrahasználása — ne hozzunk létre újat későn.
        let el = remoteAudio;
        if (! el && window.__chattVoiceSpeaker) {
            el = window.__chattVoiceSpeaker;
        }
        if (! el) {
            el = document.createElement('audio');
            el.autoplay = true;
            el.playsInline = true;
            el.setAttribute('playsinline', 'true');
            el.setAttribute('webkit-playsinline', 'true');
            document.body.appendChild(el);
        }

        try {
            el.removeAttribute('src');
            el.load?.();
        } catch {
            // ignore
        }

        el.muted = false;
        el.volume = 1;
        el.srcObject = new MediaStream([track]);
        if (! el.parentNode) {
            document.body.appendChild(el);
        }
        remoteAudio = el;
        window.__chattVoiceSpeaker = el;
        const play = () => el.play().catch(() => {});
        play();
        el.addEventListener('canplay', play, { once: true });
    }

    function bindRemoteTracksFromCall() {
        if (! call) {
            return;
        }
        try {
            const parts = call.participants?.() || {};
            Object.values(parts).forEach((p) => {
                if (! p || p.local) {
                    return;
                }
                const track = p.tracks?.audio?.persistentTrack
                    || p.tracks?.audio?.track
                    || null;
                if (track && track.readyState === 'live') {
                    attachRemoteAudio(track);
                }
            });
        } catch {
            // ignore
        }
        kickRemotePlayback();
    }

    async function destroyCall() {
        const current = call;
        call = null;
        if (! current) {
            return;
        }
        try {
            await current.leave();
        } catch {
            // ignore
        }
        try {
            await current.destroy();
        } catch {
            // ignore
        }
    }

    function sendClientReady() {
        if (! call) {
            return;
        }
        try {
            call.sendAppMessage({
                label: 'rtvi-ai',
                type: 'client-ready',
                id: `cr-${Date.now()}`,
                data: { version: '1.0.0' },
            }, '*');
        } catch {
            // ignore — hang nélkül is működhet
        }
    }

    return {
        /**
         * @param {{
         *   dailyRoom: string,
         *   dailyToken: string,
         *   audioTrack?: MediaStreamTrack|null,
         *   startMuted?: boolean,
         * }} creds
         */
        async start(creds) {
            stopped = false;
            active = true;
            handlers.onState('connecting');

            const Daily = await loadDaily();
            handlers.onTiming?.('daily_ready');
            if (stopped) {
                return;
            }

            // Az előmelegített sáv átadásával a join nem kér újra mikrofont.
            warmTrack = creds.audioTrack?.readyState === 'live' ? creds.audioTrack : null;

            call = Daily.createCallObject({
                audioSource: warmTrack ?? true,
                videoSource: false,
                subscribeToTracksAutomatically: true,
            });

            call.on('track-started', (ev) => {
                if (! active || stopped || ! ev?.track || ev.track.kind !== 'audio') {
                    return;
                }
                if (ev.participant?.local) {
                    return;
                }

                attachRemoteAudio(ev.track);
                handlers.onTiming?.('bot_audio_track');
                if (active && ! stopped) {
                    handlers.onState('listening');
                }
            });

            call.on('track-stopped', (ev) => {
                if (! active || stopped) {
                    return;
                }
                if (ev?.track?.kind === 'audio' && ! ev.participant?.local) {
                    handlers.onState('listening');
                }
            });

            call.on('app-message', (ev) => {
                if (! active || stopped) {
                    return;
                }
                const parsed = parseVoiceAppMessage(ev?.data);
                if (! parsed) {
                    return;
                }
                if (parsed.type === 'user-transcription') {
                    // Előbb a vendég sora, utána a „gondolkodik” buborék — különben a válasz a kérdés fölé kerül.
                    handlers.onUserTranscript?.(parsed.text, parsed.final !== false);
                    if (parsed.final) {
                        handlers.onState('processing');
                    }

                    return;
                }
                if (parsed.type === 'bot-text') {
                    handlers.onBotText?.(parsed.text, parsed.final !== false);

                    return;
                }
                if (parsed.type === 'lead_form' && parsed.form_id && parsed.form_payload) {
                    handlers.onLeadForm?.({
                        form_id: parsed.form_id,
                        form_payload: parsed.form_payload,
                    });

                    return;
                }
                if (parsed.type === 'handover_status' && parsed.status) {
                    handlers.onHandoverStatus?.({
                        status: parsed.status,
                        message: parsed.message ?? null,
                        bot_silenced: parsed.bot_silenced === true,
                        end_voice: parsed.end_voice === true,
                        voice_to_text_notice: parsed.voice_to_text_notice ?? null,
                    });

                    return;
                }
                if (parsed.type === 'bot-tts-started') {
                    bindRemoteTracksFromCall();
                    kickRemotePlayback();
                    handlers.onState('speaking');

                    return;
                }
                if (parsed.type === 'bot-tts-stopped') {
                    handlers.onState('listening');
                }
            });

            call.on('error', (ev) => {
                handlers.onError(ev?.errorMsg || ev?.error?.msg || 'Daily hiba');
            });

            call.on('left-meeting', () => {
                if (stopped) {
                    return;
                }
                active = false;
                cleanupAudio();
                handlers.onState('idle');
                handlers.onEnded?.();
            });

            call.on('joined-meeting', () => {
                if (! active || stopped) {
                    return;
                }
                handlers.onTiming?.('joined');
                sendClientReady();
                handlers.onReady?.();
                handlers.onState('listening');
                // Biztosítsuk a távoli hang feliratkozást
                try {
                    const parts = call?.participants?.() || {};
                    Object.values(parts).forEach((p) => {
                        if (p && ! p.local && p.session_id) {
                            call.updateParticipant(p.session_id, {
                                setSubscribedTracks: { audio: true, video: false },
                            });
                        }
                    });
                } catch {
                    // ignore
                }
            });

            call.on('participant-joined', (ev) => {
                if (! active || stopped || ! ev?.participant || ev.participant.local) {
                    return;
                }
                try {
                    call.updateParticipant(ev.participant.session_id, {
                        setSubscribedTracks: { audio: true, video: false },
                    });
                } catch {
                    // ignore
                }
            });

            const startMuted = creds.startMuted === true;

            try {
                await call.join({
                    url: creds.dailyRoom,
                    token: creds.dailyToken,
                    startAudioOff: startMuted,
                    startVideoOff: true,
                });
                if (! startMuted) {
                    try {
                        await call.setLocalAudio(true);
                    } catch {
                        // ignore
                    }
                }
            } catch (err) {
                await destroyCall();
                active = false;
                cleanupAudio();
                stopWarmTrack();
                handlers.onState('idle');
                throw err;
            }

            if (stopped) {
                await destroyCall();
                cleanupAudio();
                stopWarmTrack();
                handlers.onState('idle');
            }
        },

        /**
         * Szöveges forduló indítása a hívásban (RTVI send-text) — pl. belső jel a foglalás után.
         *
         * @param {string} content
         * @returns {boolean}
         */
        sendText(content) {
            if (! call || stopped || ! content) {
                return false;
            }
            try {
                call.sendAppMessage({
                    label: 'rtvi-ai',
                    type: 'send-text',
                    id: `st-${Date.now()}`,
                    data: { content, options: { run_immediately: true, audio_response: true } },
                }, '*');

                return true;
            } catch {
                return false;
            }
        },

        /** Vonal megnyitása a köszönés után (némán csatlakozott hívásnál). */
        unmute() {
            if (! call || stopped) {
                return;
            }
            try {
                call.setLocalAudio(true);
            } catch {
                // ignore
            }
            // Távoli TTS sáv / play újrapróbálás (autoplay policy után).
            bindRemoteTracksFromCall();
            kickRemotePlayback();
        },

        async stop() {
            stopped = true;
            active = false;
            cleanupAudio();
            await destroyCall();
            stopWarmTrack();
            handlers.onState('idle');
        },

        get active() {
            return active && ! stopped;
        },
    };
}
